/**
 * Purpose:
 *   Hook to manage menu expansion state and active item detection.
 *
 * Notes:
 *   - Tracks which expandable sections are open
 *   - Detects active menu item based on current pathname
 */

'use client';

import { useCallback, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import type { MenuItemConfig } from '@/types/menu';
import { isMenuItemActiveForPath } from '../(utils)/matchMenuItemPath';
import { useMenuExpansion } from './useMenuExpansion';

export interface UseMenuProps {
  items: MenuItemConfig[];
}

export interface UseMenuReturn {
  /** Set of expanded menu item IDs */
  expandedItems: Set<string>;
  /** Toggle expansion state of a menu item */
  toggleExpanded: (itemId: string) => void;
  /** Check if a menu item is currently active */
  isActive: (item: MenuItemConfig) => boolean;
  /** Check if a menu item or any of its children is active */
  isParentActive: (item: MenuItemConfig) => boolean;
  /** Optimistically align expansion state with a clicked menu route */
  handleNavigate: (href: string) => void;
  /** Current pathname */
  pathname: string;
}

export function useMenu({ items }: UseMenuProps): UseMenuReturn {
  const pathname = usePathname();
  const { menuPathname, expandedItems, toggleExpanded, handleNavigate } =
    useMenuExpansion({
      items,
      pathname,
      navigationPathname: pathname,
    });

  const isActive = useCallback(
    (item: MenuItemConfig): boolean => {
      return isMenuItemActiveForPath(item, menuPathname);
    },
    [menuPathname]
  );

  const isParentActive = useCallback(
    (item: MenuItemConfig): boolean => {
      if (item.children) {
        return item.children.some((child) => isActive(child));
      }
      return isActive(item);
    },
    [isActive]
  );

  return useMemo(
    () => ({
      expandedItems,
      toggleExpanded,
      isActive,
      isParentActive,
      handleNavigate,
      pathname,
    }),
    [
      expandedItems,
      toggleExpanded,
      isActive,
      isParentActive,
      handleNavigate,
      pathname,
    ]
  );
}
